/**
 * Pure model of «مسیر پیش‌فاکتور تا مالی». Turns the `GET .../quotes/:ref/flow`
 * payload into the five stages the quote page draws, and says which one is
 * next. No React, no I/O — unit-tested directly.
 *
 *  پیش‌فاکتور → سفارش فروش → تحویل → فاکتور فروش → ثبت در مالی (سند درآمد + دریافتنی)
 *
 * Invoicing is delivery-backed (ADR-013): a quote never becomes an invoice
 * directly, so the model never offers a shortcut around the order/delivery.
 */
import type { QuoteFlow } from '../../lib/types/crm.js';

export type FlowStepState = 'done' | 'current' | 'todo' | 'locked';

export interface FlowStepLink {
  readonly to: string;
  readonly label: string;
}

export interface FlowStep {
  readonly key: 'quote' | 'order' | 'delivery' | 'invoice' | 'finance';
  readonly title: string;
  readonly state: FlowStepState;
  readonly detail: string;
  readonly links: readonly FlowStepLink[];
}

export type FlowNextAction =
  | {
      readonly kind: 'send' | 'accept' | 'convert' | 'deliver' | 'issue-invoice' | 'submit-journal';
    }
  | { readonly kind: 'done' | 'none' };

const INVOICE_STATUS_LABEL: Readonly<Record<string, string>> = {
  draft: 'پیش‌نویس',
  submitted: 'ارسال‌شده برای تأیید',
  approved: 'تأییدشده',
  posted: 'ثبت قطعی',
  reversed: 'برگشت‌خورده',
};

export function invoiceStatusLabel(status: string): string {
  return INVOICE_STATUS_LABEL[status] ?? 'نامشخص';
}

export function buildQuoteFlow(flow: QuoteFlow): {
  readonly steps: readonly FlowStep[];
  readonly next: FlowNextAction;
} {
  const quote = flow.quote;
  const orders = flow.orders.status === 'ready' ? flow.orders.items : [];
  const invoices = flow.invoices.status === 'ready' ? flow.invoices.items : [];
  const liveOrders = orders.filter((o) => o.status !== 'cancelled');
  const order = liveOrders[0];
  const ordersHidden = flow.orders.status === 'forbidden';
  const invoicesHidden = flow.invoices.status === 'forbidden';

  const quoteDone = quote.status === 'accepted' || order !== undefined;
  const quoteStep: FlowStep = {
    key: 'quote',
    title: 'پیش‌فاکتور',
    state: quoteDone
      ? 'done'
      : quote.status === 'rejected' || quote.status === 'expired'
        ? 'locked'
        : 'current',
    detail: quote.statusLabel,
    links: [],
  };

  const orderStep: FlowStep = ordersHidden
    ? {
        key: 'order',
        title: 'سفارش فروش',
        state: 'locked',
        detail: 'دسترسی مشاهدهٔ سفارش ندارید.',
        links: [],
      }
    : order
      ? {
          key: 'order',
          title: 'سفارش فروش',
          state: 'done',
          detail: `${order.ref} · ${order.statusLabel}`,
          links: [{ to: `crm/orders/${order.ref}`, label: 'مشاهدهٔ سفارش' }],
        }
      : {
          key: 'order',
          title: 'سفارش فروش',
          state: quoteDone || quote.status === 'sent' ? 'current' : 'todo',
          detail: 'هنوز سفارشی از این پیش‌فاکتور ثبت نشده است.',
          links: [],
        };

  const delivered = order?.fulfillment.deliveryStatus;
  const deliveryStep: FlowStep = !order
    ? { key: 'delivery', title: 'تحویل کالا', state: 'todo', detail: 'پس از ثبت سفارش.', links: [] }
    : {
        key: 'delivery',
        title: 'تحویل کالا',
        state: delivered === 'fully_delivered' ? 'done' : 'current',
        detail: order.fulfillment.statusLabel,
        links: [{ to: `crm/orders/${order.ref}`, label: 'رزرو و تحویل' }],
      };

  const invoiceTotal = invoices.filter((i) => i.status !== 'reversed');
  const invoiceStep: FlowStep = invoicesHidden
    ? {
        key: 'invoice',
        title: 'فاکتور فروش',
        state: 'locked',
        detail: 'دسترسی مشاهدهٔ فاکتور ندارید.',
        links: [],
      }
    : invoiceTotal.length > 0
      ? {
          key: 'invoice',
          title: 'فاکتور فروش',
          state: delivered === 'fully_delivered' ? 'done' : 'current',
          detail: invoiceTotal
            .map(
              (i) => `${i.officialNumber ?? 'بدون شمارهٔ رسمی'} · ${invoiceStatusLabel(i.status)}`,
            )
            .join('، '),
          links: invoiceTotal.map((i) => ({
            to: `crm/invoices/${i.invoiceId}`,
            label: i.officialNumber ? `فاکتور ${i.officialNumber}` : 'مشاهدهٔ فاکتور',
          })),
        }
      : {
          key: 'invoice',
          title: 'فاکتور فروش',
          state:
            delivered === 'partially_delivered' || delivered === 'fully_delivered'
              ? 'current'
              : 'todo',
          detail:
            delivered === 'partially_delivered' || delivered === 'fully_delivered'
              ? 'کالای تحویل‌شده آمادهٔ صدور فاکتور است.'
              : 'فاکتور بر پایهٔ تحویل واقعی کالا صادر می‌شود.',
          links: order
            ? [{ to: `crm/orders/${order.ref}?issue=invoice`, label: 'صدور فاکتور' }]
            : [],
        };

  const postedAll = invoiceTotal.length > 0 && invoiceTotal.every((i) => i.status === 'posted');
  const financeStep: FlowStep = invoicesHidden
    ? {
        key: 'finance',
        title: 'ثبت در مالی',
        state: 'locked',
        detail: 'دسترسی مشاهدهٔ مالی ندارید.',
        links: [],
      }
    : invoiceTotal.length === 0
      ? {
          key: 'finance',
          title: 'ثبت در مالی',
          state: 'todo',
          detail: 'با صدور فاکتور، سند درآمد (پیش‌نویس) و مطالبهٔ مشتری در مالی ساخته می‌شود.',
          links: [],
        }
      : {
          key: 'finance',
          title: 'ثبت در مالی',
          state: postedAll ? 'done' : 'current',
          detail: postedAll
            ? 'سند درآمد قطعی شده و مطالبه در حساب‌های دریافتنی است.'
            : 'سند درآمد پیش‌نویس است؛ پس از تأیید و ثبت قطعی در دفاتر قرار می‌گیرد.',
          links: invoiceTotal.flatMap((i) => [
            { to: `finance/journals/${i.revenueJournalId}`, label: 'سند درآمد' },
            { to: `finance/ar/${i.arOpenItemId}`, label: 'مطالبه (دریافتنی)' },
          ]),
        };

  let next: FlowNextAction = { kind: 'none' };
  if (quote.status === 'draft') next = { kind: 'send' };
  else if (quote.status === 'sent' && !order) next = { kind: 'convert' };
  else if (quote.status === 'accepted' && !order) next = { kind: 'convert' };
  else if (order && delivered !== 'fully_delivered' && invoiceTotal.length === 0)
    next = { kind: 'deliver' };
  else if (order && invoiceTotal.length === 0) next = { kind: 'issue-invoice' };
  else if (invoiceTotal.length > 0 && !postedAll) next = { kind: 'submit-journal' };
  else if (postedAll) next = { kind: 'done' };

  return { steps: [quoteStep, orderStep, deliveryStep, invoiceStep, financeStep], next };
}
